import { useEffect, useRef } from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
`;

const NumbersGrid = styled.div`
  display: grid;
  ${({ $isPassword }) =>
    $isPassword
      ? `
    grid-template-columns: repeat(3, 66px);
    grid-auto-rows: 66px;
  `
      : `
    grid-template-columns: repeat(3, 86px);
    grid-auto-rows: 86px;
  `}
  gap: 10px;
`;

const DigitKey = styled.button`
  ${({ $isPassword }) =>
    $isPassword
      ? `
    width: 66px;
    height: 66px;
  `
      : `
    width: 86px;
    height: 86px;
  `}
  ${({ $darkMode }) =>
    $darkMode
      ? `
    background-color: var(--color-latex10);
    color: var(--color-white);
  `
      : `
    background-color: var(--color-white);
    color: var(--color-black);
  `}
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 300;
  font-size: 48px;
  line-height: 150%;
  cursor: pointer;
  transition: 0.1s;
  font-family: "Rubik", sans-serif;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  user-select: none;
  &:active {
    opacity: 0.1;
  }
`;

const EmptyKey = styled.div`
  ${({ $isPassword }) =>
    $isPassword
      ? `
    width: 66px;
    height: 66px;
  `
      : `
    width: 86px;
    height: 86px;
  `}
  ${({ $darkMode }) =>
    $darkMode
      ? `
    background-color: var(--color-latex10);
  `
      : `
    background-color: var(--color-white);
  `}
  border-radius: 8px;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  user-select: none;
`;

const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BackspaceKey = styled.button`
  ${({ $isPassword }) =>
    $isPassword
      ? `
    width: 66px;
    height: 66px;
  `
      : `
    width: 86px;
    height: 86px;
  `}
  border-radius: 8px;
  background-color: var(--color-danger);
  color: var(--color-white);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: 0.1s;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  &:active {
    opacity: 0.75;
  }
`;

const EnterKey = styled.button`
  ${({ $isPassword }) =>
    $isPassword
      ? `
    width: 66px;
    height: 220px;
  `
      : `
    width: 86px;
    height: 279px;
  `}
  border-radius: 8px;
  background: radial-gradient(circle at center, #fd9c2d 0%, #ff8800 80%);
  color: var(--color-white);
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 20px;
  border: none;
  cursor: pointer;
  transition: 0.1s;
  box-shadow: 3px 7px 11px rgba(0, 0, 0, 0.25);
  &:active {
    opacity: 0.9;
  }
  &:disabled {
    background: var(--color-grey65);
    cursor: not-allowed;
    opacity: 1;
  }
`;

/* Texto vertical usando writing-mode para mostrar texto de arriba hacia abajo */
const VerticalText = styled.span`
  writing-mode: vertical-lr;
  text-orientation: upright;
  letter-spacing: 5px;
  line-height: 1;
`;

const ArrowIcon = ({ color = "white", width = 24, height = 18 }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 39 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.791924 13.6248C0.0108757 14.4059 0.0108757 15.6722 0.791924 16.4533L13.5198 29.1812C14.3009 29.9622 15.5672 29.9622 16.3483 29.1812C17.1293 28.4001 17.1293 27.1338 16.3483 26.3528L5.03456 15.0391L16.3483 3.72535C17.1293 2.94431 17.1293 1.67798 16.3483 0.896927C15.5672 0.115878 14.3009 0.115878 13.5198 0.896927L0.791924 13.6248ZM38.5195 13.0391L2.20614 13.0391V17.0391L38.5195 17.0391V13.0391Z"
      fill={color}
    />
  </svg>
);

const Keyboard = ({
  value,
  onChange,
  onEnter,
  maxLength = 8,
  isPassword = false,
  enterText = "INGRESÁ", // Texto del botón Enter, se muestra en vertical
  darkMode = false,
  enableFrom = 3,
}) => {
  // Buffer local del valor actual. Se actualiza sincrónicamente en cada
  // tecla para evitar perder dígitos cuando el usuario tipea muy rápido
  // (los clicks se apilan antes de que React re-renderee con la prop nueva).
  const valueRef = useRef(String(value || ""));

  useEffect(() => {
    valueRef.current = String(value || "");
  }, [value]);

  const append = (digit) => {
    if (valueRef.current.length >= maxLength) return;
    const nuevo = valueRef.current + String(digit);
    valueRef.current = nuevo;
    onChange(nuevo);
  };

  const backspace = () => {
    const nuevo = valueRef.current.slice(0, -1);
    valueRef.current = nuevo;
    onChange(nuevo);
  };

  const layout = [7, 8, 9, 4, 5, 6, 1, 2, 3, null, 0, null];
  const canEnter = String(value || "").length >= enableFrom;

  return (
    <Wrapper role="application" aria-label="Teclado numérico">
      <NumbersGrid $isPassword={isPassword}>
        {layout.map((item, idx) =>
          item === null ? (
            <EmptyKey key={idx} $isPassword={isPassword} $darkMode={darkMode} />
          ) : (
            <DigitKey
              key={idx}
              onClick={() => append(item)}
              aria-label={`Número ${item}`}
              $isPassword={isPassword}
              $darkMode={darkMode}
            >
              {item}
            </DigitKey>
          )
        )}
      </NumbersGrid>
      <ActionsColumn>
        <BackspaceKey
          onClick={backspace}
          aria-label="Borrar"
          $isPassword={isPassword}
        >
          <ArrowIcon color="white" width={26} height={20} />
        </BackspaceKey>
        <EnterKey
          disabled={!canEnter}
          onClick={() => canEnter && onEnter()}
          aria-label={enterText}
          $isPassword={isPassword}
        >
          <VerticalText>{enterText}</VerticalText>
        </EnterKey>
      </ActionsColumn>
    </Wrapper>
  );
};

export default Keyboard;
